/**
 * useAutoSaveField — the shared auto-save model for editable preference text
 * fields (T30.5). Settings and the first-run wizard share one save model:
 * editable preference fields auto-save — no inline Save button. A field commits
 * on blur or Enter; an untouched field never writes; an invalid / unchanged
 * value is gated out so a half-typed postal code or a no-op commit issues no
 * spurious write.
 *
 * This hook owns ONLY field-value auto-save. Commands, destructive actions, and
 * modal edit-sessions keep their explicit buttons — they are deliberate actions,
 * not field saves, and do not run through here.
 *
 * Toggles / selects (segmented choice, switches, chips) write immediately at
 * their own change handler — they have nothing to debounce and no partial state,
 * so they don't need this hook. This hook is for the text inputs (household
 * name, postal code, address) where a commit point (blur / Enter) is the right
 * gate.
 *
 * Contract:
 *   - `value` / `onChange` make the field controlled by the caller.
 *   - `onCommit(value)` runs the existing writer (and its side-effects) when the
 *     field commits with a changed, valid value. The caller's writer is
 *     unchanged — this hook only decides *when* to call it.
 *   - `validate` gates the commit: return false and the commit is skipped (no
 *     write). Default: any non-empty trimmed value is valid; empty is allowed
 *     (some fields legitimately clear).
 *   - `initial` is the last-saved value; a commit equal to it (after trim) is a
 *     no-op and issues no write.
 *   - `saved` flips true briefly after a successful commit so the caller can
 *     show a "Saved" affordance; it resets on the next edit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseAutoSaveFieldOptions<T extends string> {
  /** Last-saved value. A commit equal to this (trimmed) is a no-op. */
  initial: T;
  /** Runs the existing writer when the field commits with a changed, valid value. */
  onCommit: (value: T) => void | Promise<void>;
  /**
   * Gate the commit. Return false → skip the write (e.g. a half-typed postal
   * code). Default: empty is allowed, anything non-empty is valid.
   */
  validate?: (value: T) => boolean;
  /** ms before the "Saved" flag auto-resets. Default 2000. */
  savedResetMs?: number;
}

export interface UseAutoSaveFieldResult<T extends string> {
  /** Current draft value (controlled). */
  value: T;
  /** Update the draft (clears the "Saved" flag). */
  setValue: (value: T) => void;
  /** Commit handler for `onBlur`. */
  onBlur: () => void;
  /** Commit handler for `onKeyDown` (Enter commits). */
  onKeyDown: (e: { key: string; preventDefault: () => void }) => void;
  /** True briefly after a successful commit; reset on the next edit. */
  saved: boolean;
  /** Manually clear the "Saved" flag. */
  clearSaved: () => void;
}

export function useAutoSaveField<T extends string>(
  options: UseAutoSaveFieldOptions<T>,
): UseAutoSaveFieldResult<T> {
  const { initial, onCommit, validate, savedResetMs = 2000 } = options;
  const [value, setValueState] = useState<T>(initial);
  const [saved, setSaved] = useState(false);
  // The last value we actually committed (or the seeded initial). A re-commit of
  // the same value is a no-op — guards against blur-after-Enter double writes
  // and against a blur that didn't change anything.
  const committedRef = useRef<string>(initial.trim());
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reseed when the saved value changes underneath us (e.g. the household query
  // refetched with a server-canonicalized value) AND the user isn't mid-edit.
  // Keyed on `initial` only: we don't want to clobber an active edit, which the
  // value-vs-committed guard inside protects. `value` is read but intentionally
  // not a dependency — re-seeding on every keystroke would fight the edit.
  useEffect(() => {
    if (value.trim() === committedRef.current) {
      committedRef.current = initial.trim();
      setValueState(initial);
    }
  }, [initial, value]);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const setValue = useCallback((next: T) => {
    setValueState(next);
    setSaved(false);
  }, []);

  const clearSaved = useCallback(() => setSaved(false), []);

  const commit = useCallback(() => {
    const trimmed = value.trim() as T;
    // No-op: nothing changed since the last commit / seed.
    if (trimmed === committedRef.current) return;
    // Validity gate: a half-typed / invalid value never writes.
    if (validate && !validate(trimmed)) return;
    committedRef.current = trimmed;
    void Promise.resolve(onCommit(trimmed)).then(() => {
      setSaved(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setSaved(false), savedResetMs);
    });
  }, [value, validate, onCommit, savedResetMs]);

  const onBlur = useCallback(() => commit(), [commit]);

  const onKeyDown = useCallback(
    (e: { key: string; preventDefault: () => void }) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      }
    },
    [commit],
  );

  return { value, setValue, onBlur, onKeyDown, saved, clearSaved };
}
